import { Component, Element, Host, State, h } from '@stencil/core';
import { HTMLStencilElement } from '@stencil/core/internal';
import { Unsubscribe, store } from '@stencil/redux';
import { MessageVariant } from '@sfc/types';
import { Language, SigningMethod, SourceName } from '../../enums/const-enums';
import {
  setActiveMethod,
  setPreferredMethod,
} from '../../redux/actions/method-action';
import { RootState, initialStore } from '../../redux/store';
import {
  focusSignElement,
  getSignElementById,
  isInViewport,
} from '../../utils/utils';
import {
  RassNotificationTranslations,
  rassNotificationTranslations,
} from './i18n/rass-notification.i18n';

@Component({
  tag: 'rass-notification',
  styleUrl: 'rass-notification.scss',
  shadow: false,
})
export class RassNotification {
  @Element() element!: HTMLStencilElement;
  @State() assetsPath: string;
  @State() sourceName: SourceName;
  @State() language: Language;
  @State() type: MessageVariant;
  @State() header: string;
  @State() body: string;
  @State() activeSignMethod: SigningMethod;
  @State() allowedSignMethods: Array<SigningMethod>;
  @State() isSignInitialized: boolean;
  setActiveMethod!: typeof setActiveMethod;
  setPreferredMethod!: typeof setPreferredMethod;
  unsubscribe!: Unsubscribe;

  componentWillLoad(): void {
    store.setStore(initialStore);
    store.mapDispatchToProps(this, {
      setActiveMethod,
      setPreferredMethod,
    });
    store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { assetsPath, language, sourceName, isSignInitialized },
        methods: { activeSignMethod, allowedSignMethods },
        notification: { type, header, body },
      } = state;
      return {
        assetsPath,
        language,
        sourceName,
        isSignInitialized,
        type,
        header,
        body,
        activeSignMethod,
        allowedSignMethods,
      };
    });
  }

  componentDidRender(): void {
    if (!this.isRuntimeNotificationShown) {
      return;
    }
    const element = getSignElementById<HTMLElement>('notification');
    if (!element) return;
    if (!isInViewport(element)) {
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY - 57;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
    focusSignElement(
      'runtime-notification-container',
      { preventScroll: true },
      1000,
    );
  }

  get isScannerlessNotificationShown(): boolean {
    return (
      !this.isRuntimeNotificationShown &&
      this.activeSignMethod === SigningMethod.RABO_SCANNER &&
      this.sourceName === SourceName.OTHER
    );
  }

  get isRuntimeNotificationShown(): boolean {
    return !!this.header || !!this.body;
  }

  get runTimeNotification(): void {
    return (
      <sfc-notification
        type={this.type}
        header={this.header}
        role="alert"
        tabIndex={-1}
        id="runtime-notification-container"
      >
        <div id="runtime-notification" innerHTML={this.body} />
      </sfc-notification>
    ) as void;
  }

  get scannerlessNotification(): void {
    return (
      <div class="qr-recommendation">
        <sfc-notification
          type="info"
          header={this.translations.notifications.SCANNERLESS.header}
        >
          {this.translations.notifications.SCANNERLESS.body}
        </sfc-notification>
      </div>
    ) as void;
  }

  get translations(): RassNotificationTranslations {
    return (
      rassNotificationTranslations[this.language] ||
      rassNotificationTranslations[Language.EN]
    );
  }

  render(): void {
    return (
      <Host class="section-notification">
        <style>@import url({`'${this.assetsPath}css/sfc-style.css'`});</style>
        <div class="sfc-container body-width" role="form">
          <div
            id="notification"
            class={`notification-${this.sourceName}-${this.activeSignMethod}`}
          >
            <div class="sfc-row sfc-justify-content-center sfc-margin-block-end-50">
              <div class="sfc-col-12">
                <rass-ost
                  assets-path={this.assetsPath}
                  language={this.language}
                  source-name={this.sourceName}
                />
                {this.isRuntimeNotificationShown && this.body
                  ? this.runTimeNotification
                  : ''}
                {this.isScannerlessNotificationShown
                  ? this.scannerlessNotification
                  : ''}
              </div>
            </div>
          </div>
        </div>
      </Host>
    ) as void;
  }
}
